import type {
  MaterialNodeSpec,
  OperationNodeSpec,
  Checkpoint,
  Duration,
  Constituent,
  Archetype,
} from '@/graph/domain/types';
import type { GraphEditorState } from '@/graph-store/types';
import { generateId } from '@/graph/utils/id';
import {
  propagateSimpleComposition,
  propagateTransferComposition,
  propagateComplexComposition,
} from '@/graph-store/compositionService';
import { propagateDownstream } from './helpers/propagateDownstream';

// ─── Types ──────────────────────────────────────────────────────────────────

type GraphData = GraphEditorState['graphData'];

export interface InsertStepPayload {
  anchorMaterialId: string;
  archetype: Archetype;
  operationTypeId?: string;
  displayName: string;
  dataExpected?: boolean;
  parameters: Record<string, unknown>;
  checkpoints: Checkpoint[];
  duration?: Duration;
  operationTemplate?: {
    operationTypeId: string;
    parameters?: Array<{ parameterName?: string; value?: unknown; unit?: string; fieldType?: string; required?: boolean; tags?: string[] }>;
    hideParamsMap?: Record<string, boolean>;
    translationTemplate?: string;
  };
  transferConfig?: {
    anchorRole: 'source' | 'destination';
    secondMaterialId?: string;
    createNewMaterial?: { name: string; containerName: string; containerType: string };
    quantity: { value: number; unit: string };
  };
  complexConfig?: {
    additionalInputs: string[];
    outputMaterialIds: string[];
  };
}

export interface InsertStepResult {
  operationId: string;
  newMaterialIds: string[];
  rewireTargetId: string;
}

// ─── Main Function ──────────────────────────────────────────────────────────

export function insertStep(graphData: GraphData, payload: InsertStepPayload): InsertStepResult {
  const anchor = graphData.materialNodes[payload.anchorMaterialId];
  if (!anchor) throw new Error(`Anchor material ${payload.anchorMaterialId} not found`);

  // ── 1. Find downstream edges BEFORE any mutations ──
  const edgeIdsToRewire = Object.keys(graphData.flowEdges).filter((edgeId) => {
    const edge = graphData.flowEdges[edgeId];
    return edge.sourceId === payload.anchorMaterialId;
  });

  // If TRANSFER with existing destination material, also collect its downstream edges
  let destEdgeIdsToRewire: string[] = [];
  if (payload.archetype === 'TRANSFER' && payload.transferConfig?.secondMaterialId) {
    let destMaterialForRewire = payload.transferConfig.secondMaterialId;
    const destMaterial = graphData.materialNodes[destMaterialForRewire];
    
    // Check if destination is a reminder/output of a COMPLEX operation
    // If it is, find the original input material instead
    for (const opId in graphData.operationNodes) {
      const op = graphData.operationNodes[opId];
      if (op.archetype === 'COMPLEX') {
        // Check if destMaterialForRewire is an output of this COMPLEX
        const isOutput = Object.entries(graphData.flowEdges).some(
          ([_, e]) => e.sourceId === opId && e.targetId === destMaterialForRewire
        );
        
        if (isOutput) {
          // This material is a reminder/output from a COMPLEX
          // Find the original input to this COMPLEX with same name
          const inputEdges = Object.entries(graphData.flowEdges).filter(
            ([_, e]) => e.targetId === opId
          );
          
          for (const [_, edge] of inputEdges) {
            const inputMat = graphData.materialNodes[edge.sourceId];
            // Find the input that has the same name as the destination
            if (inputMat && inputMat.name === destMaterial?.name) {
              // Found original input - use that for rewiring instead
              destMaterialForRewire = edge.sourceId;
              break;
            }
          }
          break;
        }
      }
    }
    
    // Collect downstream edges from the actual material we should rewire
    destEdgeIdsToRewire = Object.keys(graphData.flowEdges).filter((edgeId) => {
      const edge = graphData.flowEdges[edgeId];
      return edge.sourceId === destMaterialForRewire;
    });
  }

  // ── 2. Create OperationNode ──
  const newOpId = generateId('operation');

  graphData.operationNodes[newOpId] = {
    id: newOpId,
    archetype: payload.archetype,
    operationTypeId: payload.operationTypeId,
    displayName: payload.displayName,
    dataExpected: payload.dataExpected ?? false,
    parameters: payload.parameters,
    checkPoints: payload.checkpoints,
    duration: payload.duration,
    operationTemplate: payload.operationTemplate,
    creationOrder: 0, // temporary — renumberAfterInsert will set the correct value
    // Position is owned by the HierarchicalLayout; no manual seeding.
  };

  renumberAfterInsert(graphData, payload.anchorMaterialId, newOpId);

  // If anchor is inside a group, inherit groupId
  const anchorGroupId = findGroupIdForMaterial(graphData, payload.anchorMaterialId);
  if (anchorGroupId) {
    graphData.operationNodes[newOpId].groupId = anchorGroupId;
    if (graphData.groups[anchorGroupId]) {
      // Create a new array instead of mutating — cloneGraphData uses shallow
      // spreads so stepIds may be shared with the undo snapshot.
      graphData.groups[anchorGroupId] = {
        ...graphData.groups[anchorGroupId],
        stepIds: [...(graphData.groups[anchorGroupId].stepIds || []), newOpId],
      };
    }
  }

  // ── 3. Create output MaterialNode(s) ──
  const newMaterialIds: string[] = [];
  let rewireTargetId: string;

  if (payload.archetype === 'TRANSFER' && payload.transferConfig) {
    // Correct the transfer config if we had to adjust the destination material
    let transferConfig = payload.transferConfig;
    if (destEdgeIdsToRewire.length > 0) {
      const sourceMatOfFirstEdge = graphData.flowEdges[destEdgeIdsToRewire[0]]?.sourceId;
      if (sourceMatOfFirstEdge && sourceMatOfFirstEdge !== payload.transferConfig.secondMaterialId) {
        transferConfig = {
          ...payload.transferConfig,
          secondMaterialId: sourceMatOfFirstEdge,
        };
      }
    }

    const result = handleTransferInsert(graphData, anchor, { ...payload, transferConfig }, newOpId);
    newMaterialIds.push(...result.newMaterialIds);
    rewireTargetId = result.rewireTargetId;
  } else if (payload.archetype === 'COMPLEX' && payload.complexConfig) {
    const result = handleComplexInsert(graphData, anchor, payload, newOpId);
    newMaterialIds.push(...result.newMaterialIds);
    rewireTargetId = result.rewireTargetId;
  } else {
    // TRANSFORM / MEASURE — 1 input, 1 output
    const result = handleSimpleInsert(graphData, anchor, payload, newOpId);
    newMaterialIds.push(...result.newMaterialIds);
    rewireTargetId = result.rewireTargetId;
  }

  // ── 4. REWIRE downstream edges ──
  // Use edge IDs collected BEFORE mutations so we only rewire pre-existing edges.
  for (const edgeId of edgeIdsToRewire) {
    graphData.flowEdges[edgeId].sourceId = rewireTargetId;
  }

  // For TRANSFER with existing destination, also rewire destination's edges to mergedId
  if (payload.archetype === 'TRANSFER' && payload.transferConfig?.secondMaterialId && destEdgeIdsToRewire.length > 0) {
    // For TRANSFER, handler returns [remainderId, mergedId]
    const mergedId = newMaterialIds[1];
    for (const edgeId of destEdgeIdsToRewire) {
      graphData.flowEdges[edgeId].sourceId = mergedId;
    }
  }

  // ── 5. Propagate downstream updates ──
  // Skip materials just created — their values are already correct.
  const skipIds = new Set(newMaterialIds);
  propagateDownstream(graphData, rewireTargetId, { skipIds });

  return { operationId: newOpId, newMaterialIds, rewireTargetId };
}

// ─── Step Renumbering ───────────────────────────────────────────────────────

/**
 * Assigns the new operation's creationOrder based on its topological position
 * (right after the op that produces the anchor material) and shifts all
 * subsequent operations' creationOrder by +1.
 */
function renumberAfterInsert(graphData: GraphData, anchorMaterialId: string, newOpId: string): void {
  // Find the operation that produces the anchor material
  let producingOpId: string | undefined;
  let producingOpOrder = 0;
  for (const edge of Object.values(graphData.flowEdges)) {
    if (edge.targetId === anchorMaterialId && graphData.operationNodes[edge.sourceId]) {
      producingOpId = edge.sourceId;
      producingOpOrder = graphData.operationNodes[producingOpId].creationOrder ?? 0;
      break;
    }
  }

  // Check if producing op is in a group
  const producingOpGroupId = producingOpId && graphData.operationNodes[producingOpId]?.groupId;

  if (producingOpGroupId) {
    // Case 1: New op is being inserted into a group
    const group = graphData.groups[producingOpGroupId];
    if (group && producingOpId) {
      // Find the index of the producing op in the group
      const producingOpIndex = group.stepIds.indexOf(producingOpId);
      if (producingOpIndex >= 0) {
        // Insert new op right after the producing op
        const insertAtIndex = producingOpIndex + 1;
        
        // The group.stepIds was already updated (new op was appended)
        // Reorder to put it in the right position
        const stepIds = group.stepIds.filter((id) => id !== newOpId);
        stepIds.splice(insertAtIndex, 0, newOpId);

        // Update the group with new stepIds
        group.stepIds = stepIds;

        // Update creationOrder and orderInGroup for all ops in the group
        let currentOrder = producingOpOrder;
        for (let i = 0; i < stepIds.length; i++) {
          const opId = stepIds[i];
          if (i > 0) {
            // Gap-fill: ensure each step has creationOrder > previous
            const prevOp = graphData.operationNodes[stepIds[i - 1]];
            const prevOrder = prevOp.creationOrder ?? currentOrder;
            currentOrder = Math.max(currentOrder + 1, prevOrder + 1);
          }
          graphData.operationNodes[opId].creationOrder = currentOrder;
          graphData.operationNodes[opId].orderInGroup = i;
        }

        // Shift all ops AFTER this group
        const lastOrderInGroup = currentOrder;
        for (const op of Object.values(graphData.operationNodes)) {
          if (!group.stepIds.includes(op.id!) && (op.creationOrder ?? 0) > lastOrderInGroup) {
            op.creationOrder = (op.creationOrder ?? 0) + 1;
          }
        }
      }
    }
  } else {
    // Case 2: New op is not in a group — use original global insertion logic
    const insertionOrder = producingOpOrder + 1;

    // Shift all existing ops at or after the insertion point (excluding the new op)
    for (const op of Object.values(graphData.operationNodes)) {
      if (op.id !== newOpId && (op.creationOrder ?? 0) >= insertionOrder) {
        op.creationOrder = (op.creationOrder ?? 0) + 1;
      }
    }

    graphData.operationNodes[newOpId].creationOrder = insertionOrder;
  }
}

// ─── Archetype-Specific Handlers ────────────────────────────────────────────

function handleSimpleInsert(
  graphData: GraphData,
  anchor: MaterialNodeSpec,
  payload: InsertStepPayload,
  newOpId: string,
): { newMaterialIds: string[]; rewireTargetId: string } {
  const postStateId = generateId('material');

  graphData.materialNodes[postStateId] = {
    id: postStateId,
    name: anchor.name,
    container: { ...anchor.container },
    constituents: anchor.constituents ? [...anchor.constituents.map((c) => ({ ...c }))] : [],
    quantity: computeTransformOutputQuantity(anchor, payload),
    composition: propagateSimpleComposition(anchor.id!, anchor.name, newOpId),
    // Position is owned by the HierarchicalLayout; no manual seeding.
  };

  // FlowEdges: anchor → newOp, newOp → postState
  const e1Id = generateId('edge');
  graphData.flowEdges[e1Id] = { id: e1Id, sourceId: anchor.id!, targetId: newOpId };
  const e2Id = generateId('edge');
  graphData.flowEdges[e2Id] = { id: e2Id, sourceId: newOpId, targetId: postStateId };

  return { newMaterialIds: [postStateId], rewireTargetId: postStateId };
}

function handleTransferInsert(
  graphData: GraphData,
  anchor: MaterialNodeSpec,
  payload: InsertStepPayload,
  newOpId: string,
): { newMaterialIds: string[]; rewireTargetId: string } {
  const toAmount = (value: unknown): number => {
    const num = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const config = payload.transferConfig!;
  const role = config.anchorRole ?? 'source';

  const remainderId = generateId('material');
  const mergedId = generateId('material');

  // Determine source and destination materials
  let sourceMat: MaterialNodeSpec;
  let destMat: MaterialNodeSpec | undefined;

  if (role === 'source') {
    sourceMat = anchor;
    destMat = config.secondMaterialId ? graphData.materialNodes[config.secondMaterialId] : undefined;
  } else {
    sourceMat = graphData.materialNodes[config.secondMaterialId!]!;
    destMat = anchor;
  }

  const transferQty = toAmount(config.quantity?.value);
  const transferUnit = config.quantity?.unit ?? sourceMat.quantity?.value?.units ?? 'mL';

  // Composition/lineage for the two post-states. destNodeId is the pre-existing
  // destination when present, otherwise the merged output is itself the dest.
  const transferComposition = propagateTransferComposition(
    sourceMat.id!,
    sourceMat.name,
    destMat?.id ?? mergedId,
    destMat?.name,
    newOpId,
    {
      value: { amount: transferQty, units: transferUnit as any },
      type: sourceMat.quantity?.type ?? 'Volume',
    },
  );

  // ── Create remainder (source post-state, reduced quantity) ──
  const sourceQty = toAmount(sourceMat.quantity?.value?.amount);
  graphData.materialNodes[remainderId] = {
    id: remainderId,
    name: sourceMat.name,
    container: { ...sourceMat.container },
    constituents: sourceMat.constituents ? [...sourceMat.constituents.map((c) => ({ ...c }))] : [],
    quantity: {
      value: { amount: Math.max(0, sourceQty - transferQty), units: transferUnit as any },
      type: sourceMat.quantity?.type ?? 'Volume',
    },
    isReminder: true, // Mark this as a reminder node
    composition: transferComposition.sourcePostComposition,
    // Position is owned by the HierarchicalLayout; no manual seeding.
  };

  // ── Create merged (destination post-state, increased quantity) ──
  const destQty = toAmount(destMat?.quantity?.value?.amount);
  const mergedConstituents = mergeConstituents(
    sourceMat.constituents ?? [],
    transferQty,
    destMat?.constituents ?? [],
    destQty,
  );

  graphData.materialNodes[mergedId] = {
    id: mergedId,
    name: destMat ? `${destMat.name}` : sourceMat.name,
    container: destMat
      ? { ...destMat.container }
      : {
          id: generateId('container'),
          name: config.createNewMaterial?.containerName ?? 'New Container',
          containerType: (config.createNewMaterial?.containerType ?? 'Tube') as any,
          copyName: false,
        },
    constituents: mergedConstituents,
    quantity: {
      value: { amount: destQty + transferQty, units: transferUnit as any },
      type: sourceMat.quantity?.type ?? 'Volume',
    },
    composition: transferComposition.destPostComposition,
    sourceMaterials: [
      {
        sourceVertexId: sourceMat.id!,
        name: sourceMat.name,
        container: { ...sourceMat.container },
        notes: null,
      },
    ],
    mergedNode: true,
    // Position is owned by the HierarchicalLayout; no manual seeding.
  };

  // ── FlowEdges ──
  const e1 = generateId('edge');
  graphData.flowEdges[e1] = { id: e1, sourceId: sourceMat.id!, targetId: newOpId };
  const e2 = generateId('edge');
  graphData.flowEdges[e2] = { id: e2, sourceId: newOpId, targetId: remainderId };
  const e3 = generateId('edge');
  graphData.flowEdges[e3] = { id: e3, sourceId: newOpId, targetId: mergedId };
  if (config.secondMaterialId) {
    const e4 = generateId('edge');
    graphData.flowEdges[e4] = { id: e4, sourceId: config.secondMaterialId, targetId: newOpId };
  }

  // ── Archetype data on the operation ──
  // The backend requires transferData.destinationMaterialId for TRANSFER ops;
  // when there is no pre-existing destination, the merged output is the
  // destination. Post-state ids make the spec self-describing for run-stage.
  const op = graphData.operationNodes[newOpId];
  op.transferRow = {
    id: generateId('trow'),
    sourceMaterialId: sourceMat.id!,
    destinationMaterialId: destMat?.id ?? mergedId,
    sourcePostMaterialId: remainderId,
    destinationPostMaterialId: mergedId,
    quantity: {
      value: { amount: transferQty, units: transferUnit as any },
      type: sourceMat.quantity?.type ?? 'Volume',
    },
  };
  op.transferOptions = {
    quantityType: sourceMat.quantity?.type ?? 'Volume',
    transferEntireVolume: false,
    sameVolume: false,
    advancedOptionsEnabled: false,
  };

  // Rewire target depends on role
  const rewireTargetId = role === 'source' ? remainderId : mergedId;

  return { newMaterialIds: [remainderId, mergedId], rewireTargetId };
}

function handleComplexInsert(
  graphData: GraphData,
  anchor: MaterialNodeSpec,
  payload: InsertStepPayload,
  newOpId: string,
): { newMaterialIds: string[]; rewireTargetId: string } {
  const config = payload.complexConfig!;
  const outputMaterialIds = config.outputMaterialIds ?? [];

  // All materials feeding the COMPLEX op — used for each output's lineage.
  const inputIds = [anchor.id!, ...config.additionalInputs];
  const outputComposition = propagateComplexComposition(inputIds, newOpId);

  const newMaterialIds: string[] = [];

  // Use provided output material IDs
  for (const outputId of outputMaterialIds) {
    newMaterialIds.push(outputId);
  }

  // FlowEdges: anchor → op
  const e1 = generateId('edge');
  graphData.flowEdges[e1] = { id: e1, sourceId: anchor.id!, targetId: newOpId };

  // Additional inputs → op
  for (const inputId of config.additionalInputs) {
    const eId = generateId('edge');
    graphData.flowEdges[eId] = { id: eId, sourceId: inputId, targetId: newOpId };
  }

  // Op → outputs
  for (const matId of newMaterialIds) {
    const eId = generateId('edge');
    graphData.flowEdges[eId] = { id: eId, sourceId: newOpId, targetId: matId };
  }

  // ── Archetype data on the operation ──
  // The backend requires at least one complexData.ioMapping entry for COMPLEX
  // ops. Default: every input feeds every output in equal fractions.
  graphData.operationNodes[newOpId].complexOptions = {
    ioMapping: inputIds.flatMap((inputMaterialId) =>
      newMaterialIds.map((outputMaterialId) => ({
        inputMaterialId,
        outputMaterialId,
        fraction: 1 / newMaterialIds.length,
      })),
    ),
  };

  // ── Create reminder nodes (one per input) ──
  // Reminders track what remains of each input after the COMPLEX operation.
  // For insert operations, reminders are what continue downstream.
  const reminderIds: string[] = [];
  for (const inputId of inputIds) {
    const inputMaterial = graphData.materialNodes[inputId];
    const reminderId = generateId('material');

    // Reminder has same properties as the input material, marked as isReminder
    graphData.materialNodes[reminderId] = {
      id: reminderId,
      name: inputMaterial.name,
      container: { ...inputMaterial.container },
      constituents: inputMaterial.constituents ? [...inputMaterial.constituents.map((c) => ({ ...c }))] : [],
      quantity: inputMaterial.quantity
        ? { ...inputMaterial.quantity, value: { ...inputMaterial.quantity.value } }
        : null,
      composition: undefined, // Will be set if needed
      isReminder: true,
      // Position is owned by the HierarchicalLayout; no manual seeding.
    };

    // Edge: op → reminder
    const eId = generateId('edge');
    graphData.flowEdges[eId] = { id: eId, sourceId: newOpId, targetId: reminderId };

    reminderIds.push(reminderId);
  }

  // Add reminders to the materials list (but they chain downstream, not the outputs)
  newMaterialIds.push(...reminderIds);

  // rewireTargetId = first reminder node (this is what chains to downstream operations)
  const rewireTargetId = reminderIds.length > 0 ? reminderIds[0] : newMaterialIds[0];

  return { newMaterialIds, rewireTargetId };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function computeTransformOutputQuantity(
  anchor: MaterialNodeSpec,
  payload: InsertStepPayload,
): MaterialNodeSpec['quantity'] {
  const yieldVal = (payload.parameters?.yield as number) ?? 1;
  const inputQty = anchor.quantity?.value?.amount ?? 0;
  return {
    value: {
      amount: inputQty * yieldVal,
      units: anchor.quantity?.value?.units ?? 'mL',
    },
    type: anchor.quantity?.type ?? 'Volume',
  };
}

function mergeConstituents(
  sourceConstituents: Constituent[],
  sourceQty: number,
  destConstituents: Constituent[],
  destQty: number,
): Constituent[] {
  const totalQty = sourceQty + destQty;
  if (totalQty === 0) return [];

  const map = new Map<string, number>();

  for (const c of sourceConstituents) {
    const contribution = (c.weight * sourceQty) / totalQty;
    map.set(c.name, (map.get(c.name) ?? 0) + contribution);
  }
  for (const c of destConstituents) {
    const contribution = (c.weight * destQty) / totalQty;
    map.set(c.name, (map.get(c.name) ?? 0) + contribution);
  }

  return Array.from(map.entries()).map(([name, weight]) => ({ name, weight }));
}

function findGroupIdForMaterial(graphData: GraphData, materialId: string): string | undefined {
  for (const edge of Object.values(graphData.flowEdges)) {
    if (edge.targetId === materialId && graphData.operationNodes[edge.sourceId]) {
      return graphData.operationNodes[edge.sourceId].groupId;
    }
  }
  return undefined;
}
